// src/pages/Booking.js
import React, { useState, useEffect, useCallback } from 'react'; // Added useCallback
import { useCart } from '../context/CartContext';
import { useNavigate, Link } from 'react-router-dom'; // Added Link
import { FaInfoCircle, FaWhatsapp, FaPhone } from 'react-icons/fa';

function Booking() {
  const { cartItems, totalPrice } = useCart();
  const navigate = useNavigate();

  // Redirect if cart is empty
  useEffect(() => {
    if (cartItems.length === 0) {
      alert('Your cart is empty. Please add repairs before booking.');
      navigate('/');
    }
  }, [cartItems, navigate]);

  // Form state management
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '',
    street: '', city: '', postcode: '',
  });
  const [loading, setLoading] = useState(false); // Loading state for submission

  // Load/Save form data from/to localStorage
  useEffect(() => {
    const storedForm = localStorage.getItem('bookingForm');
    if (storedForm) {
      try {
          const parsed = JSON.parse(storedForm);
          if (typeof parsed === 'object' && parsed !== null) {
              const relevantData = Object.keys(formData).reduce((acc, key) => {
                  if (parsed.hasOwnProperty(key)) { acc[key] = parsed[key]; }
                  return acc;
              }, {});
              setFormData(prev => ({ ...prev, ...relevantData }));
          } else { localStorage.removeItem('bookingForm'); }
      } catch (error) { console.error("Failed to parse booking form", error); localStorage.removeItem('bookingForm'); }
    }
  }, []); // Run only on mount

  useEffect(() => {
    localStorage.setItem('bookingForm', JSON.stringify(formData));
  }, [formData]);



  // Input change handler
  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Form submission handler
  const handleSubmit = useCallback((e) => { // Wrapped in useCallback
    e.preventDefault();
    if (loading) return; // Prevent double submission
    setLoading(true);

    const address = `${formData.street}, ${formData.city}, ${formData.postcode}`;
    const payload = {
      bookingData: { ...formData, address },
      cartItems: cartItems.map(item => ({ ...item, cost: Number(item.cost) })),
      totalPrice: Number(totalPrice),
    };
    localStorage.setItem('bookingInfo', JSON.stringify(payload));
    localStorage.removeItem('bookingForm');
    navigate('/payment');
    // setLoading(false); // No need to set false before navigating away
  }, [formData, cartItems, totalPrice, navigate, loading]); // Added loading to dependency

  // Render null or a loading indicator if cart is empty
  if (cartItems.length === 0) {
      return <div style={loadingOverlayStyle}>Redirecting...</div>;
  }

  return (
    <main className="page-container booking-page container py-5">
      <header className="text-center mb-5">
        <h1 className="display-4" style={{ color: '#6c757d', fontWeight: 'bold' }}>Book Your Repair</h1>
        <p className="lead text-muted">Confirm your repair items and schedule a convenient time.</p>
      </header>

      {/* Cart Summary Section */}
      <section className="cart-summary mb-5 p-4" style={sectionStyle}>
         <h2 style={sectionHeadingStyle}>Your Selected Repairs</h2>
         <ul style={{ listStyle: 'none', padding: 0, marginBottom: '1rem' }}>
           {cartItems.map((item, idx) => (
             <li key={item.id || idx} style={cartItemStyle}>
               {item.image && (<img src={item.image} alt="" style={cartItemImageStyle} />)}
               <span style={{ flexGrow: 1, color: '#6c757d' }}><strong>{item.name}</strong></span>
               <span style={{ color: '#6c757d', fontWeight: '500' }}>£{item.cost.toFixed(2)}</span>
             </li>
           ))}
         </ul>
         <hr style={{ borderColor: '#ccc' }}/>
         <div style={{...totalsStyle, fontWeight: 'bold', fontSize: '1.2rem', color: '#6c757d'}}>
            <span>Total Amount Due:</span><span>£{totalPrice.toFixed(2)}</span>
        </div>
      </section>

      {/* Need Help Section with WhatsApp & Phone */}
      <section className="mb-5 p-4" style={{...sectionStyle, backgroundColor: '#f8f9fa', textAlign: 'center'}}>
         <h2 style={sectionHeadingStyle}>Need Help Booking?</h2>
         <p style={{ color: '#6c757d', marginBottom: '1.5rem' }}>Contact us directly via WhatsApp or phone for assistance</p>
         <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
           <a
             href="https://wa.me/447944859580"
             target="_blank"
             rel="noopener noreferrer"
             style={whatsappButtonStyle}
           >
             <FaWhatsapp style={{ marginRight: '0.5rem' }} size={20} />
             WhatsApp Us
           </a>
           <a
             href="tel:+447944859580"
             style={phoneButtonStyle}
           >
             <FaPhone style={{ marginRight: '0.5rem' }} size={18} />
             Call Us Now
           </a>
         </div>
         <p style={{ fontSize: '0.9rem', color: '#6c757d', marginTop: '1rem' }}>
           💬 <strong>Live Chat</strong> is available in the bottom-right corner
         </p>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works mb-5 p-4" style={sectionStyle}>
         <h2 style={sectionHeadingStyle} className="text-center mb-3">How It Works</h2>
         <div style={howItWorksStepsContainer}>
             <div style={howItWorksStep}><span style={stepNumberStyle}>1</span>Fill in your details below.</div>
             <div style={howItWorksStep}><span style={stepNumberStyle}>2</span>Proceed to our secure payment page.</div>
             <div style={howItWorksStep}><span style={stepNumberStyle}>3</span>Receive booking confirmation via email.</div>
             <div style={howItWorksStep}><span style={stepNumberStyle}>4</span>Bring in or send your device for repair!</div>
        </div>
      </section>

      {/* Booking Form Section */}
      <section className="booking-form-section">
        <h2 style={sectionHeadingStyle} className="mb-3">Book Your Repair</h2>
        <div className="p-4" style={sectionStyle}>
          <form className="booking-form" onSubmit={handleSubmit}>

             {/* Contact Info Fields */}
             <h3 style={{...sectionHeadingStyle, fontSize: '1.2rem', marginTop: '2rem', marginBottom: '1rem' }}>Contact Information</h3>
             <div className="mb-3">
               <label htmlFor="name" className="form-label" style={labelStyle}>Full Name *</label>
               <input type="text" id="name" name="name" className="form-control" style={inputStyle} placeholder="John Doe" required value={formData.name} onChange={handleChange}/>
             </div>
             <div className="row mb-3">
                  <div className="col-md-6 mb-3 mb-md-0">
                     <label htmlFor="email" className="form-label" style={labelStyle}>Email *</label>
                     <input type="email" id="email" name="email" className="form-control" style={inputStyle} placeholder="you@example.com" required value={formData.email} onChange={handleChange}/>
                  </div>
                   <div className="col-md-6">
                     <label htmlFor="phone" className="form-label" style={labelStyle}>Phone *</label>
                     <input type="tel" id="phone" name="phone" className="form-control" style={inputStyle} placeholder="e.g., 07123 456789" required value={formData.phone} onChange={handleChange}/>
                  </div>
             </div>

             {/* Address Fields */}
              <h3 style={{...sectionHeadingStyle, fontSize: '1.2rem', marginTop: '2rem', marginBottom: '1rem' }}>Address</h3>
             <div className="mb-3">
               <label htmlFor="street" className="form-label" style={labelStyle}>Street Address *</label>
               <input type="text" id="street" name="street" className="form-control" style={inputStyle} placeholder="123 Main Street" required value={formData.street} onChange={handleChange}/>
             </div>
              <div className="row mb-3">
                 <div className="col-md-6 mb-3 mb-md-0">
                     <label htmlFor="city" className="form-label" style={labelStyle}>City *</label>
                     <input type="text" id="city" name="city" className="form-control" style={inputStyle} placeholder="London" required value={formData.city} onChange={handleChange}/>
                 </div>
                 <div className="col-md-6">
                     <label htmlFor="postcode" className="form-label" style={labelStyle}>Postcode *</label>
                     <input type="text" id="postcode" name="postcode" className="form-control" style={inputStyle} placeholder="SW1A 0AA" required value={formData.postcode} onChange={handleChange}/>
                 </div>
             </div>

            {/* Submit Button */}
            <div className="text-center mt-4">
                 {/* Apply dynamic styles directly here */}
                 <button
                   type="submit"
                   // Combine base style with dynamic ones
                   style={{
                       ...submitButtonStyleBase, // Use the base style object
                       cursor: loading ? 'not-allowed' : 'pointer',
                       opacity: loading ? 0.7 : 1,
                   }}
                   disabled={loading}
                 >
                   {loading ? 'Processing...' : 'Proceed to Payment'}
                 </button>
            </div>
          </form>
        </div>
      </section>
    </main>
  );
}

// --- Styles defined outside component ---

// Base style for the main submit button (Yellow CTA theme)
const bwButtonStyle = {
    display: 'inline-block', alignItems: 'center', background: '#FFC107', color: '#6c757d',
    border: '2px solid #FFC107', borderRadius: '8px', padding: '0.75rem 1.5rem',
    cursor: 'pointer', fontSize: '1rem', textDecoration: 'none', fontWeight: '600',
    transition: 'all 0.3s ease', textAlign: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
};

// Specific styles for *this* page's submit button, inheriting base
const submitButtonStyleBase = {
    ...bwButtonStyle, // Inherit base styles
    fontSize: '1.1rem', // Override size
    padding: '0.8rem 2rem',
    // REMOVED dynamic cursor/opacity from here
};

// WhatsApp button style
const whatsappButtonStyle = {
    display: 'inline-flex', alignItems: 'center', background: '#25D366', color: '#fff',
    border: '2px solid #25D366', borderRadius: '8px', padding: '0.75rem 1.5rem',
    cursor: 'pointer', fontSize: '1rem', textDecoration: 'none', fontWeight: '600',
    transition: 'all 0.3s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
};

// Phone button style
const phoneButtonStyle = {
    display: 'inline-flex', alignItems: 'center', background: '#007BFF', color: '#fff',
    border: '2px solid #007BFF', borderRadius: '8px', padding: '0.75rem 1.5rem',
    cursor: 'pointer', fontSize: '1rem', textDecoration: 'none', fontWeight: '600',
    transition: 'all 0.3s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
};

// Other styles remain the same
const sectionStyle = { border: '1px solid #ccc', borderRadius: '8px', backgroundColor: '#fff', };
const sectionHeadingStyle = { fontSize: '1.4rem', fontWeight: 'bold', color: '#6c757d', marginBottom: '1rem', };
const cartItemStyle = { marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: '1rem', };
const cartItemImageStyle = { width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0, };
const totalsStyle = { display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', color: '#6c757d', fontSize: '1rem', };
const feeExplanationStyle = { fontSize: '0.85rem', color: '#6c757d', marginTop: '0.75rem', display: 'flex', alignItems: 'center', };
const howItWorksStepsContainer = { display: 'flex', flexDirection: 'column', gap: '0.75rem', };
const howItWorksStep = { padding: '0.75rem 1rem', border: '1px solid #eee', borderRadius: '4px', backgroundColor: '#f8f9fa', color: '#6c757d', display: 'flex', alignItems: 'center', };
const stepNumberStyle = { display: 'inline-flex', justifyContent: 'center', alignItems: 'center', width: '24px', height: '24px', borderRadius: '50%', backgroundColor: '#6c757d', color: '#fff', fontWeight: 'bold', marginRight: '10px', flexShrink: 0, };
const labelStyle = { fontWeight: '500', marginBottom: '0.3rem', display: 'block', color: '#6c757d', };
const inputStyle = { borderColor: '#ccc', };
const loadingOverlayStyle = { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(255, 255, 255, 0.8)', color: '#6c757d', fontSize: '1.5rem', zIndex: 1100, };

export default Booking;